/**
 * Workspace settings — default index route (/_app/settings/).
 *
 * Tabbed: Company / Localization / Notifications / Danger Zone.
 *
 * In mock mode, mutations persist to local React state and surface a toast.
 * The same handlers will swap to InstantDB mutations once wired — the form
 * value shape already matches.
 */

import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  Building2,
  Globe2,
  Hash,
  Languages,
  Monitor,
  Moon,
  RotateCcw,
  Rows3,
  Rows4,
  Sun,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TooltipProvider,
  cn,
  toast,
  useDensity,
  useTheme,
} from "@bizop/ui-kit";
import {
  COMMON_CURRENCIES,
  DEFAULT_BASE_CURRENCY,
  formatMoney,
  getCurrencyMeta,
} from "@bizop/shared/currency";

import { PageHeader } from "../../../components/PageHeader";
import { CompanyProfileForm } from "../../../components/forms/CompanyProfileForm";
import type { CompanyProfileValues } from "../../../components/forms/CompanyProfileForm";
import { isMockMode } from "../../../lib/mockMode";
import { demoStore } from "../../../lib/demoStore";
import {
  useCurrentRole,
} from "../../../lib/permissions";
import { useAppSettings, type CompanyProfile } from "../../../lib/appSettings";
import {
  previewInvoiceNumber,
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_LABELS,
  DEFAULT_PREFIXES,
  previewDocumentNumber,
  type DocumentType,
  type DocumentPrefixes,
} from "../../../lib/invoiceNumbering";

export const Route = createFileRoute("/_app/settings/")({
  component: SettingsPage,
});

const SETTINGS_KEY_LOCALE = "bizop-app-locale";

type AdminLocale = "en" | "bn";
type NotificationKey =
  | "order_approval"
  | "lc_expiry"
  | "overdue_invoices"
  | "low_stock";

const NOTIFICATION_DEFS: Array<{
  key: NotificationKey;
  title: string;
  description: string;
}> = [
  {
    key: "order_approval",
    title: "Order approval requests",
    description: "Email when a sales order is submitted for approval.",
  },
  {
    key: "lc_expiry",
    title: "LC expiry warnings",
    description: "30-day, 7-day and same-day pings for letters of credit.",
  },
  {
    key: "overdue_invoices",
    title: "Overdue invoices",
    description: "Daily digest at 9am of invoices past their due date.",
  },
  {
    key: "low_stock",
    title: "Low-stock alerts",
    description: "When SKUs drop below their warehouse reorder point.",
  },
];

const NOTIFICATION_DEFAULTS: Record<NotificationKey, boolean> = {
  order_approval: true,
  lc_expiry: true,
  overdue_invoices: true,
  low_stock: false,
};

function SettingsPage() {
  // Danger Zone → owner only.
  const role = useCurrentRole();
  const isOwner = role === "owner";

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Settings"
          description="Workspace configuration. Personal preferences live under Profile."
        />

        <Tabs defaultValue="company" className="w-full">
          <TabsList aria-label="Settings sections">
            <TabsTrigger value="company">
              <Building2 aria-hidden className="h-4 w-4 mr-1.5" />
              Company
            </TabsTrigger>
            <TabsTrigger value="numbering">
              <Hash aria-hidden className="h-4 w-4 mr-1.5" />
              Numbering
            </TabsTrigger>
            <TabsTrigger value="localization">
              <Globe2 aria-hidden className="h-4 w-4 mr-1.5" />
              Localization
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Languages aria-hidden className="h-4 w-4 mr-1.5" />
              Notifications
            </TabsTrigger>
            {isOwner ? (
              <TabsTrigger value="danger">
                <AlertTriangle aria-hidden className="h-4 w-4 mr-1.5" />
                Danger Zone
              </TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="company" className="mt-5">
            <CompanyTab />
          </TabsContent>
          <TabsContent value="numbering" className="mt-5">
            <DocumentNumberingTab />
          </TabsContent>
          <TabsContent value="localization" className="mt-5">
            <LocalizationTab />
          </TabsContent>
          <TabsContent value="notifications" className="mt-5">
            <NotificationsTab />
          </TabsContent>
          {isOwner ? (
            <TabsContent value="danger" className="mt-5">
              <DangerZoneTab />
            </TabsContent>
          ) : null}
        </Tabs>
      </div>
    </TooltipProvider>
  );
}

/* ----------------------------- Company tab ----------------------------- */

function CompanyTab() {
  const { companyProfile, updateCompanyProfile } = useAppSettings();
  const [pendingProfile, setPendingProfile] = useState<CompanyProfileValues | null>(null);
  const [currencyWarnOpen, setCurrencyWarnOpen] = useState(false);

  function handleProfileSubmit(values: CompanyProfileValues) {
    if (values.baseCurrency !== companyProfile.baseCurrency) {
      setPendingProfile(values);
      setCurrencyWarnOpen(true);
      return;
    }
    // Merge form values with existing profile to preserve fields not
    // managed by the CompanyProfileForm (e.g. documentPrefixes, invoice
    // numbering, VAT rate).
    updateCompanyProfile({ ...companyProfile, ...values });
    toast.success("Company profile saved");
  }

  return (
    <div className="flex flex-col gap-5">
      <SettingsSection
        title="Company profile"
        description={
          isMockMode()
            ? "Mock mode — changes persist for this session only."
            : "Saved to companyProfile in InstantDB."
        }
      >
        <CompanyProfileForm
          initialValues={companyProfile}
          onSubmit={handleProfileSubmit}
        />
      </SettingsSection>

      <InvoiceNumberingSection />

      <VatRateSection />

      <Dialog
        open={currencyWarnOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCurrencyWarnOpen(false);
            setPendingProfile(null);
          }
        }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>
              Change base currency to {pendingProfile?.baseCurrency}?
            </DialogTitle>
            <DialogDescription>
              <span className="flex items-start gap-2">
                <AlertTriangle className="size-4 shrink-0 mt-0.5 text-(--warning)" />
                <span>
                  <strong>Critical:</strong> Changing the base currency affects ALL
                  financial reports across the entire system. All historical amounts
                  displayed in {companyProfile.baseCurrency} will now be shown as{" "}
                  {pendingProfile?.baseCurrency}. This does not convert existing
                  figures — it changes the label only. This can make reports appear
                  incorrect. Proceed only if you are starting fresh.
                </span>
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setCurrencyWarnOpen(false);
                setPendingProfile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (pendingProfile) {
                  updateCompanyProfile({ ...companyProfile, ...pendingProfile });
                  toast.success("Company profile saved");
                }
                setCurrencyWarnOpen(false);
                setPendingProfile(null);
              }}
            >
              I understand, change currency
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ----------------------- Invoice numbering section ----------------------- */

function InvoiceNumberingSection() {
  const { companyProfile, updateCompanyProfile } = useAppSettings();

  const [prefix, setPrefix] = useState(companyProfile.invoicePrefix ?? "INV");
  const [yearFormat, setYearFormat] = useState<"full" | "short">(
    companyProfile.invoiceYearFormat ?? "full",
  );
  const [padLength, setPadLength] = useState(
    companyProfile.invoicePadLength ?? 6,
  );

  const preview = previewInvoiceNumber({ prefix, yearFormat, padLength });

  const isDirty =
    prefix !== (companyProfile.invoicePrefix ?? "INV") ||
    yearFormat !== (companyProfile.invoiceYearFormat ?? "full") ||
    padLength !== (companyProfile.invoicePadLength ?? 6);

  function handleSave() {
    if (prefix.trim().length === 0) {
      toast.error("Prefix cannot be empty");
      return;
    }
    updateCompanyProfile({
      ...companyProfile,
      invoicePrefix: prefix,
      invoiceYearFormat: yearFormat,
      invoicePadLength: padLength,
    });
    toast.success("Invoice numbering saved");
  }

  return (
    <SettingsSection
      title="Invoice numbering"
      description="Configure how invoice numbers are generated. Changes apply to new invoices only — existing numbers are preserved."
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Prefix */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invoice-prefix">Prefix</Label>
            <Input
              id="invoice-prefix"
              value={prefix}
              maxLength={10}
              onChange={(e) => setPrefix(e.target.value.toUpperCase())}
              placeholder="INV"
            />
            <span className="text-[11px] text-(--text-tertiary)">
              Max 10 characters, uppercase.
            </span>
          </div>

          {/* Year format */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invoice-year-format">Year format</Label>
            <Select
              value={yearFormat}
              onValueChange={(v) => setYearFormat(v as "full" | "short")}
            >
              <SelectTrigger id="invoice-year-format" aria-label="Year format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Full year (2026)</SelectItem>
                <SelectItem value="short">Short year (26)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pad length */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invoice-pad-length">Counter digits</Label>
            <Input
              id="invoice-pad-length"
              type="number"
              min={4}
              max={8}
              value={padLength}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v >= 4 && v <= 8) setPadLength(v);
              }}
            />
            <span className="text-[11px] text-(--text-tertiary)">
              Range: 4 to 8 digits.
            </span>
          </div>
        </div>

        {/* Live preview */}
        <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2.5 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
              Preview
            </div>
            <div className="font-mono text-[14px] tabular-nums text-(--text-primary) mt-0.5">
              {preview}
            </div>
          </div>
          <Badge variant="neutral">next invoice</Badge>
        </div>

        {/* Save */}
        <div className="flex justify-end">
          <Button type="button" onClick={handleSave} disabled={!isDirty}>
            Save numbering
          </Button>
        </div>
      </div>
    </SettingsSection>
  );
}

/* ----------------------- Document numbering tab ----------------------- */

function DocumentNumberingTab() {
  const { companyProfile, updateCompanyProfile } = useAppSettings();

  const currentPrefixes: DocumentPrefixes =
    companyProfile.documentPrefixes ?? { ...DEFAULT_PREFIXES };

  const [prefixes, setPrefixes] = useState<DocumentPrefixes>({
    ...currentPrefixes,
  });
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const isDirty = DOCUMENT_TYPES.some(
    (t) => prefixes[t] !== currentPrefixes[t],
  );

  function handlePrefixChange(type: DocumentType, value: string) {
    setPrefixes((prev) => ({ ...prev, [type]: value.toUpperCase() }));
  }

  function handleSave() {
    for (const t of DOCUMENT_TYPES) {
      if (prefixes[t].trim().length === 0) {
        toast.error(
          `Prefix for ${DOCUMENT_TYPE_LABELS[t]} cannot be empty`,
        );
        return;
      }
    }
    updateCompanyProfile({
      ...companyProfile,
      documentPrefixes: { ...prefixes },
    });
    toast.success("Document numbering prefixes saved");
  }

  function handleResetCounters() {
    setResetConfirmOpen(false);
    toast.info(
      "Counters are derived from existing documents. Delete or void documents to reset the sequence.",
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <SettingsSection
        title="Document numbering"
        description="Configure the prefix for each document type. The format is {PREFIX}{YEAR}-{COUNTER}. Changes apply to new documents only."
      >
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DOCUMENT_TYPES.map((type) => (
              <div key={type} className="flex flex-col gap-1.5">
                <Label htmlFor={`prefix-${type}`}>
                  {DOCUMENT_TYPE_LABELS[type]}
                </Label>
                <Input
                  id={`prefix-${type}`}
                  value={prefixes[type]}
                  maxLength={10}
                  onChange={(e) => handlePrefixChange(type, e.target.value)}
                  placeholder={DEFAULT_PREFIXES[type]}
                />
                <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2.5 py-1.5 flex items-center justify-between">
                  <div className="font-mono text-[12px] tabular-nums text-(--text-secondary)">
                    {previewDocumentNumber(prefixes[type])}
                  </div>
                  <span className="text-[10px] uppercase tracking-wide text-(--text-tertiary)">
                    preview
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setResetConfirmOpen(true)}
              className="text-[12px]"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset counters
            </Button>
            <Button type="button" onClick={handleSave} disabled={!isDirty}>
              Save prefixes
            </Button>
          </div>
        </div>
      </SettingsSection>

      <Dialog
        open={resetConfirmOpen}
        onOpenChange={(open) => {
          if (!open) setResetConfirmOpen(false);
        }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Reset document counters?</DialogTitle>
            <DialogDescription>
              <span className="flex items-start gap-2">
                <AlertTriangle className="size-4 shrink-0 mt-0.5 text-(--warning)" />
                <span>
                  Document counters are derived automatically from existing
                  documents. To reset a counter to 000001, you would need to
                  void or remove all existing documents of that type for the
                  current year. This action cannot create gaps or duplicates
                  in the sequence.
                </span>
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setResetConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleResetCounters}>
              I understand
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------- Default VAT rate section -------------------- */

/** Convert a percentage major-unit string ("15" or "12.5") to E4 BigInt. */
function vatPercentToE4(major: string): bigint {
  const trimmed = major.trim();
  if (trimmed === "") return 0n;
  const cleaned = trimmed.replace(/[^0-9.]/g, "");
  const [w = "", f = ""] = cleaned.split(".");
  const wholeStr = w === "" ? "0" : w;
  const frac = (f + "0000").slice(0, 4);
  return BigInt(wholeStr) * 10_000n + BigInt(frac || "0");
}

/** Render a BigInt E4-scaled percentage back to a major-unit string. */
function vatE4ToPercentString(e4: bigint): string {
  const negative = e4 < 0n;
  const abs = negative ? -e4 : e4;
  const whole = abs / 10_000n;
  const frac = (abs % 10_000n).toString().padStart(4, "0");
  let trimmed = frac.replace(/0+$/, "");
  if (trimmed.length === 0) return `${negative ? "-" : ""}${whole.toString()}`;
  return `${negative ? "-" : ""}${whole.toString()}.${trimmed}`;
}

function VatRateSection() {
  const { companyProfile, updateCompanyProfile } = useAppSettings();
  const initial = vatE4ToPercentString(companyProfile.defaultVatRatePercentE4);
  const [vatInput, setVatInput] = useState(initial);

  const parsed = vatPercentToE4(vatInput);
  const isDirty = parsed !== companyProfile.defaultVatRatePercentE4;

  function handleSave() {
    updateCompanyProfile({
      ...companyProfile,
      defaultVatRatePercentE4: parsed,
    });
    toast.success("Default VAT rate saved");
  }

  return (
    <SettingsSection
      title="Default VAT rate"
      description="Applied to new invoices when the 'Apply VAT' toggle is enabled on the form."
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="vat-rate">VAT rate</Label>
            <div className="relative">
              <Input
                id="vat-rate"
                inputMode="decimal"
                value={vatInput}
                onChange={(e) => setVatInput(e.target.value)}
                className="pr-7 font-mono"
                placeholder="15"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-(--text-tertiary)">
                %
              </span>
            </div>
            <span className="text-[11px] text-(--text-tertiary)">
              Stored as basis-point × 100 (E4) — e.g. 15% is BigInt(150000).
            </span>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="button" onClick={handleSave} disabled={!isDirty}>
            Save VAT rate
          </Button>
        </div>
      </div>
    </SettingsSection>
  );
}

/* --------------------------- Localization tab --------------------------- */

function LocalizationTab() {
  const { mode, setMode } = useTheme();
  const { density, setDensity } = useDensity();
  const [locale, setLocale] = useState<AdminLocale>(() => {
    if (typeof window === "undefined") return "en";
    return (localStorage.getItem(SETTINGS_KEY_LOCALE) as AdminLocale) || "en";
  });
  const [dateFormat, setDateFormat] = useState<"medium" | "short" | "iso">(
    "medium",
  );
  const [currencyDisplay, setCurrencyDisplay] = useState<
    "symbol" | "code" | "name"
  >("symbol");

  const meta = getCurrencyMeta(DEFAULT_BASE_CURRENCY);
  const localeTag = locale === "bn" ? "bn-BD" : "en-BD";

  function persistLocale(next: AdminLocale) {
    setLocale(next);
    if (typeof window !== "undefined") {
      localStorage.setItem(SETTINGS_KEY_LOCALE, next);
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <SettingsSection
        title="Default locale"
        description="Used for formatting numbers, dates and currency in admin views."
      >
        <Select value={locale} onValueChange={(v) => persistLocale(v as AdminLocale)}>
          <SelectTrigger aria-label="Default locale">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="en">English (en-BD)</SelectItem>
            <SelectItem value="bn">বাংলা (bn-BD)</SelectItem>
          </SelectContent>
        </Select>
        <div className="mt-3 rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3">
          <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
            Date preview
          </div>
          <div className="mt-1 font-mono text-[14px] tabular-nums text-(--text-primary)">
            {formatDateExample(dateFormat, localeTag)}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {(["medium", "short", "iso"] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              aria-pressed={dateFormat === opt}
              onClick={() => setDateFormat(opt)}
              className={cn(
                "rounded-md border px-2 py-1.5 text-[12px] capitalize",
                dateFormat === opt
                  ? "border-(--accent) bg-(--accent-soft) text-(--accent)"
                  : "border-(--border-subtle) bg-(--bg-canvas) text-(--text-secondary) hover:bg-(--bg-hover)",
              )}
            >
              {opt}
            </button>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection
        title="Currency display"
        description="Workspace base currency. Used for cross-currency rollups."
      >
        <div className="flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2.5">
          <div>
            <div className="text-[13px] font-medium text-(--text-primary)">
              {meta?.name ?? DEFAULT_BASE_CURRENCY}{" "}
              <span className="font-mono text-[12px] text-(--text-tertiary)">
                ({DEFAULT_BASE_CURRENCY})
              </span>
            </div>
            <div className="text-[12px] text-(--text-tertiary)">
              Locale {localeTag} · Symbol {meta?.symbol ?? "—"}
            </div>
          </div>
          <div className="font-mono text-[14px] font-medium tabular-nums text-(--text-primary)">
            {formatMoney(1_234_500_00n, DEFAULT_BASE_CURRENCY, {
              locale: localeTag,
              display: currencyDisplay === "name" ? "name" : currencyDisplay,
            })}
          </div>
        </div>
        <div className="mt-3">
          <Select
            value={currencyDisplay}
            onValueChange={(v) =>
              setCurrencyDisplay(v as "symbol" | "code" | "name")
            }
          >
            <SelectTrigger aria-label="Currency display">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="symbol">Symbol (৳1,234.50)</SelectItem>
              <SelectItem value="code">Code (BDT 1,234.50)</SelectItem>
              <SelectItem value="name">Name (1,234.50 Bangladeshi Takas)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="mt-2 text-[11px] text-(--text-tertiary)">
          To change the actual base currency, edit it in Company → Base currency.
          BizOP supports {COMMON_CURRENCIES.length} currencies out of the box.
        </p>
      </SettingsSection>

      <SettingsSection
        title="Theme"
        description="Choose how BizOP looks across the admin app."
      >
        <div className="grid grid-cols-3 gap-2">
          <ToggleTile
            icon={Sun}
            label="Light"
            active={mode === "light"}
            onClick={() => setMode("light")}
          />
          <ToggleTile
            icon={Moon}
            label="Dark"
            active={mode === "dark"}
            onClick={() => setMode("dark")}
          />
          <ToggleTile
            icon={Monitor}
            label="System"
            active={mode === "system"}
            onClick={() => setMode("system")}
          />
        </div>
      </SettingsSection>

      <SettingsSection
        title="Density"
        description="Compact mode tightens row heights for data-heavy screens."
      >
        <div className="grid grid-cols-2 gap-2">
          <ToggleTile
            icon={Rows3}
            label="Comfortable"
            active={density === "comfortable"}
            onClick={() => setDensity("comfortable")}
          />
          <ToggleTile
            icon={Rows4}
            label="Compact"
            active={density === "compact"}
            onClick={() => setDensity("compact")}
          />
        </div>
      </SettingsSection>
    </div>
  );
}

function formatDateExample(
  variant: "medium" | "short" | "iso",
  localeTag: string,
): string {
  const now = new Date();
  if (variant === "iso") return now.toISOString().slice(0, 19) + "Z";
  return new Intl.DateTimeFormat(localeTag, {
    dateStyle: variant === "medium" ? "medium" : "short",
    timeStyle: "short",
  }).format(now);
}

/* --------------------------- Notifications tab --------------------------- */

function NotificationsTab() {
  const [prefs, setPrefs] = useState<Record<NotificationKey, boolean>>(
    NOTIFICATION_DEFAULTS,
  );

  return (
    <SettingsSection
      title="Notification channels"
      description="Choose what we email or push to you."
    >
      <ul className="flex flex-col divide-y divide-(--border-subtle) rounded-md border border-(--border-subtle) bg-(--bg-canvas)">
        {NOTIFICATION_DEFS.map((n) => (
          <li
            key={n.key}
            className="flex items-start justify-between gap-3 p-3"
          >
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-(--text-primary)">
                {n.title}
              </div>
              <div className="text-[12px] text-(--text-tertiary)">
                {n.description}
              </div>
            </div>
            <Switch
              checked={prefs[n.key]}
              onCheckedChange={(v) => {
                setPrefs((p) => ({ ...p, [n.key]: v }));
                toast.info(
                  v
                    ? `Enabled: ${n.title}`
                    : `Disabled: ${n.title}`,
                );
              }}
              aria-label={n.title}
            />
          </li>
        ))}
      </ul>
    </SettingsSection>
  );
}

/* --------------------------- Danger zone tab --------------------------- */

function DangerZoneTab() {
  const [confirming, setConfirming] = useState(false);

  function handleResetDemo() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    demoStore.clear();
    toast.success("Demo data reset — reloading…");
    setTimeout(() => window.location.reload(), 800);
  }

  return (
    <SettingsSection
      title="Danger zone"
      description="Destructive operations. Confirm carefully before proceeding."
    >
      <ul className="flex flex-col gap-2.5">
        {/* Demo-mode only: wipe localStorage and reload with seed data */}
        {isMockMode() ? (
          <li className="flex items-start justify-between gap-3 rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3">
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-(--text-primary)">
                Reset demo data
              </div>
              <div className="text-[12px] text-(--text-tertiary)">
                Clears all locally-saved edits and reloads the seed data. Use
                this to restore the demo to its original state.
              </div>
            </div>
            <Button
              type="button"
              variant={confirming ? "destructive" : "outline"}
              onClick={handleResetDemo}
              onBlur={() => setConfirming(false)}
              className="shrink-0"
            >
              {confirming ? "Confirm reset" : "Reset"}
            </Button>
          </li>
        ) : null}
        <DangerRow
          title="Export full audit log"
          description="Download all audit entries as JSONL. Large operation."
          ctaLabel="Export"
        />
        <DangerRow
          title="Delete workspace"
          description="Removes every record across all 54 tables. Cannot be undone."
          ctaLabel="Delete"
          destructive
        />
      </ul>
    </SettingsSection>
  );
}

function DangerRow({
  title,
  description,
  ctaLabel,
  destructive,
}: {
  title: string;
  description: string;
  ctaLabel: string;
  destructive?: boolean;
}) {
  return (
    <li className="flex items-start justify-between gap-3 rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3">
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-(--text-primary)">
          {title}
        </div>
        <div className="text-[12px] text-(--text-tertiary)">{description}</div>
      </div>
      <Button
        type="button"
        variant={destructive ? "destructive" : "outline"}
        disabled
        aria-label={`${ctaLabel} — disabled in mock mode`}
      >
        {ctaLabel}
      </Button>
    </li>
  );
}

/* ----------------------------- Shared bits ----------------------------- */

function SettingsSection({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[14px] font-semibold tracking-tight text-(--text-primary)">
            {title}
          </h2>
          {description ? (
            <p className="mt-0.5 text-[12px] text-(--text-secondary)">
              {description}
            </p>
          ) : null}
        </div>
        {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

function ToggleTile({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      className={cn(
        "flex flex-col items-center justify-center gap-1.5 rounded-md border px-3 py-3 transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--border-focus)",
        active
          ? "border-(--accent) bg-(--accent-soft) text-(--accent)"
          : "border-(--border-subtle) bg-(--bg-canvas) text-(--text-secondary) hover:bg-(--bg-hover) hover:text-(--text-primary)",
      )}
    >
      <Icon aria-hidden className="h-4 w-4" />
      <span className="text-[12px] font-medium">{label}</span>
    </button>
  );
}
